import { FC, useContext, useEffect } from 'react';

import { useMutation } from '@apollo/client';
import { Button, Input, Modal, NumberInput, Toggle } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';
import { NavLink } from 'react-router-dom';

import { ActionSchedule } from 'components';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { SettingsContext } from 'settings';
import { getRandomNumber, secondsToWeekDays, weekDaysToSeconds } from 'utils';

import { CREATE_DISCOUNT, UPDATE_DISCOUNT, DELETE_DISCOUNT } from '../schema';
import { ALL_WEEK_DAYS, DEFAULT_VALUES } from './constants';
import s from './DiscountManager.module.scss';
import { IDiscountManagerProps, IFormValues } from './interfaces';
import { prepareUpdatePayload } from './utils';

export const DiscountManager: FC<IDiscountManagerProps> = ({
  discount,
  onSuccess,
  onClose,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { telegramChannelId },
    },
  } = useContext(SettingsContext);

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty, dirtyFields },
  } = useForm<IFormValues>({ mode: 'onChange', defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (!discount) {
      reset(DEFAULT_VALUES);
      return;
    }

    const hasSellPeriods = discount?.schedules && discount.schedules.length > 0;

    reset({
      ...DEFAULT_VALUES,
      title: discount.title,
      days: hasSellPeriods
        ? discount.schedules.map(
            (schedule) => secondsToWeekDays(schedule.start).day
          )
        : [],
      hours:
        hasSellPeriods && discount?.schedules[0]
          ? {
              start: secondsToWeekDays(discount.schedules[0]?.start)?.hour,
              end: secondsToWeekDays(discount.schedules[0]?.end)?.hour,
            }
          : { start: '', end: '' },
      active: discount.active,
      dont_use_on_holidays: discount.dont_use_on_holidays,
      value: discount.value,
      is_tracked: discount.is_tracked,
    });

    trigger();
  }, [discount, reset, trigger]);

  const [createDiscount, { loading: createDiscountLoading }] = useMutation(
    CREATE_DISCOUNT,
    {
      onCompleted: () => {
        onSuccess();
        reset(DEFAULT_VALUES);
        notification.info({
          t,
          title: t('discount.created'),
          type: 'success',
          dismiss: 5,
        });
      },
      fetchPolicy: 'no-cache',
    }
  );

  const [updateDiscount, { loading: updateDiscountLoading }] = useMutation(
    UPDATE_DISCOUNT,
    {
      onCompleted: () => {
        notification.info({
          t,
          title: t('discount.updated'),
          type: 'success',
          dismiss: 5,
        });
        onSuccess();
      },
      fetchPolicy: 'no-cache',
    }
  );

  const [deleteDiscount, { loading: deleteDiscountLoading }] = useMutation(
    DELETE_DISCOUNT,
    {
      onCompleted: () => {
        notification.info({
          t,
          title: t('discount.deleted'),
          type: 'success',
          dismiss: 5,
        });
        onSuccess();
      },
      fetchPolicy: 'no-cache',
    }
  );

  return (
    <Modal className={s.container} onClose={onClose}>
      <form
        onSubmit={handleSubmit((values) => {
          const validatedDays = values?.days.length
            ? values?.days
            : ALL_WEEK_DAYS;
          const validatedHours = values?.hours?.start
            ? values?.hours
            : { start: '00:00', end: '00:00' };

          const schedules = weekDaysToSeconds(validatedHours, validatedDays);

          const payload = {
            title: values.title,
            value: values.value,
            is_tracked: values.is_tracked,
            active: values.active,
            schedules: schedules,
            dont_use_on_holidays: values.dont_use_on_holidays,
          };

          if (discount) {
            const updatePayload = prepareUpdatePayload(values, dirtyFields);
            updateDiscount({
              variables: {
                updateDiscountId: discount.id,
                input: updatePayload,
              },
            }).catch(console.error);
            return;
          }

          createDiscount({
            variables: {
              input: {
                ...payload,
              },
            },
          }).catch(console.error);
        })}
      >
        <header>{t(discount ? 'discount.update' : 'discount.create')}</header>
        <div className={s['fields-container']}>
          <Controller
            render={({ field: { name, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                title={t('title')}
                name={`${name}-${getRandomNumber()}`}
                error={error?.message}
                placeholder={t('discount.title.placeholder')}
                maxLength={16}
              />
            )}
            name="title"
            control={control}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 3,
                message: t('errors.minLength', 3),
              },
              maxLength: {
                value: 16,
                message: t('errors.maxLength', 16),
              },
            }}
          />
          <Controller
            render={({ field: { name, ...field }, fieldState: { error } }) => (
              <NumberInput
                {...field}
                title={t('value')}
                name={`${name}-${getRandomNumber()}`}
                error={error?.message}
                placeholder={'1'}
                rightContent="%"
              />
            )}
            name="value"
            control={control}
            rules={{
              required: t('errors.required'),
              min: { value: 1, message: t('errors.min', '1%') },
              max: { value: 100, message: t('errors.max', '100%') },
            }}
          />
          <div className={s.with_info}>
            <Controller
              render={({ field }) => (
                <Toggle
                  {...field}
                  iconName="PaperPlaneIcon"
                  label={t('telegram.notification.title')}
                  disabled={!telegramChannelId?.id}
                />
              )}
              name="is_tracked"
              control={control}
            />
            {!telegramChannelId?.id && (
              <span className={s.info_text}>
                {t('tariffs.telegram.disabled.info_3')}{' '}
                <NavLink
                  state={{ selectedTab: 'INTEGRATIONS' }}
                  to={'/settings'}
                  className={s.info_link}
                >
                  {t('settings.tab.integration')}
                </NavLink>
                {' → '}
                Telegram
              </span>
            )}
          </div>
        </div>

        <div className={s.shedule_block}>
          <ActionSchedule control={control} trigger={trigger} />
          <Controller
            render={({ field }) => (
              <Toggle
                {...field}
                iconName="CalendarDayIcon"
                label={t('discounts.holiday.toggle')}
                className={s.holiday}
              />
            )}
            name="dont_use_on_holidays"
            control={control}
          />
        </div>
        <Controller
          render={({ field }) => (
            <Toggle {...field} iconName="ShutdownIcon" label={t('state')} />
          )}
          name="active"
          control={control}
        />
        <div className={s['submit-container']}>
          {discount && (
            <Button
              variant="secondary"
              fullWidth
              color="red"
              onClick={() =>
                deleteDiscount({ variables: { id: discount.id } }).catch(
                  console.error
                )
              }
            >
              {t('delete')}
            </Button>
          )}
          <Button
            type="submit"
            fullWidth
            className={s['submit-button']}
            isLoading={
              createDiscountLoading ||
              updateDiscountLoading ||
              deleteDiscountLoading
            }
            disabled={!isDirty}
          >
            {t(discount ? 'save' : 'add')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
